import { createRef, useEffect, useMemo, useRef, useState } from "react";

import { SPACING } from "../../../constants/design/spacing";
import { useRovingRadioGroup } from "../../../keyboard/useRovingRadioGroup";
import { Chevron } from "../Chevron/style";
import Dropdown from "../Dropdown";
import { DropdownItem } from "../DropdownSimple/style";
import {
  ActiveBackground,
  SegmentControlButton,
  SegmentControlButtonText,
  SegmentControlContainer,
  WidthType,
} from "./style";

export interface ISegmentedControlConfig {
  theme?: "outline";
  color?: string;
  backgroundColor?: string;
  activeBackgroundColor?: string;
  widthType?: WidthType;
  button?: {
    px?: number;
    py?: number;
    height?: number;
    fontSize: string;
  };
  borderRadius?: string;
  margin?: string;
}

export type ISegmentType = {
  type?: string;
  textColor?: string;
  disabled?: boolean;
} & (
  | {
      type?: "default";
      value: string;
      display?: string | JSX.Element;
    }
  | {
      type: "dropdown";
      value: string;
      display: string;
      items: {
        title: string;
        value: string;
        onSelectItem: () => void;
      }[];
    }
);

interface ISegmentControlProps {
  segments: ISegmentType[];
  value: string;
  onSelect?: (value: string) => void;
  config?: ISegmentedControlConfig;
  /**
   * Opt in to the radiogroup keyboard contract, using this string as the group's
   * accessible name. Segments become role="radio" with aria-checked, a roving
   * tabindex and arrow/Home/End keys.
   *
   * OPT-IN, not the default, and deliberately so: this component has 14 direct
   * consumers plus shared/BottomBar, and the e2e suite reaches its segments with
   * getByRole("button", ...). Changing the role for everyone would rewrite
   * unrelated specs and screens to fix a keyboard bug in one of them.
   *
   * Top-level rather than inside `config` because `config` is styling.
   *
   * radiogroup, not tablist: a tablist promises tabpanels, and the one caller
   * that needs this renders its body as sibling conditionals with no per-panel
   * element and no ids -- the strip is even declared after them. Advertising
   * tabs without panels would be the same defect as a combobox with no listbox.
   */
  radioGroupLabel?: string;
}

function SegmentControl({
  segments,
  value,
  onSelect,
  radioGroupLabel,
  config: {
    theme,
    color,
    backgroundColor,
    activeBackgroundColor,
    widthType = "maxContent",
    button = { px: 16, py: 12, fontSize: "14px" },
    borderRadius,
    margin,
  } = {},
}: ISegmentControlProps) {
  const controlRefs = useMemo(
    () =>
      segments.reduce<any>((acc, curr) => {
        acc[curr.value] = createRef();
        return acc;
      }, {}),
    [segments]
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeBackgroundState, setActiveBackgroundState] = useState<{
    left: number;
    top: number;
    height: number;
    width: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // The refs already exist for measuring the sliding highlight; a div with a
  // tabIndex is focusable, so they double as the roving-focus targets.
  const { itemProps } = useRovingRadioGroup({
    values: segments.map((seg) => seg.value),
    value,
    onSelect: (v) => onSelect?.(v),
    focus: (v) => controlRefs[v]?.current?.focus(),
    isDisabled: (v) => !!segments.find((seg) => seg.value === v)?.disabled,
  });
  /** role="button" unless a caller opted in, so every existing screen is unchanged. */
  const segmentA11y = (segment: ISegmentType, index: number) =>
    radioGroupLabel
      ? itemProps(segment.value, index)
      : ({ role: "button" } as const);

  useEffect(() => {
    const currentRef = controlRefs[value]?.current;

    if (!currentRef) {
      return () => {};
    }

    const handleResize = () => {
      setActiveBackgroundState({
        left: currentRef.offsetLeft,
        top: currentRef.offsetTop,
        height: currentRef.clientHeight,
        width: currentRef.clientWidth - 1,
      });
    };
    handleResize();

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [value, controlRefs, theme]);

  return (
    <SegmentControlContainer
      ref={containerRef}
      role={radioGroupLabel ? "radiogroup" : undefined}
      aria-label={radioGroupLabel}
      $color={color}
      backgroundColor={backgroundColor}
      widthType={widthType}
      style={{ borderRadius, margin }}
    >
      {segments.map((segment, segmentIndex) => {
        if (segment.type === "dropdown") {
          return (
            <SegmentControlButton
              key={segment.value}
              {...segmentA11y(segment, segmentIndex)}
              onClick={() => {
                if (segment.disabled) {
                  return;
                }
                // Selects the default option
                const defaultOption = segment.items.find(
                  ({ value: v }) => v === segment.value
                );
                defaultOption?.onSelectItem();
              }}
              ref={controlRefs[segment.value]}
              widthType={widthType}
              px={button.px}
              py={button.py}
              $height={button.height}
              backgroundColor={
                theme === "outline" && value === segment.value
                  ? activeBackgroundColor
                  : "transparent"
              }
              $disabled={segment.disabled}
              style={{ borderRadius }}
            >
              <Dropdown
                width={"100%"}
                onToggle={(open) => setDropdownOpen(open)}
                dropdownMenuContainerStyles={{
                  width: 115,
                  outline: "none",
                  marginRight: button.px ? -button.px : undefined,
                }}
                toggleStyles={{
                  padding: 0,
                  height: button.height,
                  backgroundColor: "transparent",
                  border: "none",
                }}
                title={
                  <SegmentControlButtonText
                    $color={segment.textColor ?? color}
                    style={{
                      fontSize: button.fontSize,
                    }}
                  >
                    {segment.display}
                    <Chevron
                      style={{ marginLeft: SPACING.one }}
                      direction={dropdownOpen ? "up" : "down"}
                    />
                  </SegmentControlButtonText>
                }
                items={segment.items.map(
                  ({ title, value: v, onSelectItem }) => ({
                    label: (
                      <DropdownItem
                        key={title}
                        isSelected={segment.value === v}
                      >
                        {title}
                      </DropdownItem>
                    ),
                    onSelect: (e) => {
                      e.stopPropagation();
                      onSelectItem();
                    },
                  })
                )}
              />
            </SegmentControlButton>
          );
        }
        return (
          <SegmentControlButton
            key={segment.value}
            {...segmentA11y(segment, segmentIndex)}
            onClick={(e) => {
              e.stopPropagation();
              if (!segment.disabled) {
                onSelect?.(segment.value);
              }
            }}
            ref={controlRefs[segment.value]}
            widthType={widthType}
            px={button.px}
            py={button.py}
            $height={button.height}
            backgroundColor={
              theme === "outline" && value === segment.value
                ? activeBackgroundColor
                : "transparent"
            }
            $disabled={segment.disabled}
            style={{ borderRadius }}
          >
            <SegmentControlButtonText
              $color={segment.textColor ?? color}
              style={{
                fontSize: button.fontSize,
              }}
            >
              {segment.display}
            </SegmentControlButtonText>
          </SegmentControlButton>
        );
      })}
      {/* @ts-ignore */}
      <ActiveBackground
        transition={{
          type: "keyframes",
          ease: "easeOut",
        }}
        initial={{
          height: "100%",
          width: `calc(100% / ${segments.length})`,
        }}
        animate={activeBackgroundState ?? undefined}
        //@ts-ignore
        theme={theme}
        color={theme === "outline" ? color : activeBackgroundColor}
        style={{ borderRadius }}
      />
    </SegmentControlContainer>
  );
}

export default SegmentControl;
